import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters.js';
import { downloadQuotePdf, previewQuotePdf } from '../../utils/pdf.js';
import { formatQty } from '../../utils/units.js';

import { ConvertQuoteModal } from './ConvertQuoteModal.jsx';
import { QuoteCreateModal } from './QuoteCreateModal.jsx';
import { QuoteSendModal } from './QuoteSendModal.jsx';

export function QuoteDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [convertTarget, setConvertTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const quote = useQuery({
    queryKey: ['sales', 'quotes', id],
    queryFn: () => salesApi.getQuote(id).then((r) => r.items),
  });
  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => salesApi.acceptQuote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'quotes', id] }),
  });
  const reject = useMutation({
    mutationFn: () => salesApi.rejectQuote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'quotes', id] }),
  });

  if (quote.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (quote.error || !quote.data) {
    return (
      <section className="page">
        <DetailHeader backTo="/sales/quotes" title={t('sales.quotes.title')} />
        <div className="alert danger">{quote.error?.message || 'Not found'}</div>
      </section>
    );
  }

  const q = quote.data;
  const customer = (customers.data || []).find((c) => c.id === q.customerId);

  const actions = [
    <Button key="preview" variant="ghost" onClick={() => previewQuotePdf({ quote: q, customer, settings: settings.data, t })}>
      {t('pdf.preview')}
    </Button>,
    <Button key="pdf" variant="ghost" onClick={() => downloadQuotePdf({ quote: q, customer, settings: settings.data, t })}>
      {t('invoice.print.downloadPdf')}
    </Button>,
  ];
  // Strictly linear, self-explanatory flow:
  // DRAFT → send it; SENT → record the customer's decision (or convert directly);
  // ACCEPTED → convert into a sales order.
  if (q.status === 'DRAFT') {
    actions.push(
      <Button key="edit" variant="ghost" onClick={() => setEditOpen(true)}>
        {t('common.edit')}
      </Button>,
      <Button key="send" variant="primary" onClick={() => setSendOpen(true)}>
        {t('sales.quotes.action.send')}
      </Button>,
    );
  }
  if (q.status === 'SENT') {
    actions.push(
      <Button key="reject" variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending}>
        {t('sales.quotes.action.reject')}
      </Button>,
      <Button key="accept" variant="primary" onClick={() => accept.mutate()} disabled={accept.isPending}>
        {t('sales.quotes.action.accept')}
      </Button>,
      <Button key="convert" variant="ghost" onClick={() => setConvertTarget(q)}>
        {t('sales.quotes.action.convert')}
      </Button>,
    );
  }
  if (q.status === 'ACCEPTED') {
    actions.push(
      <Button key="convert" variant="primary" onClick={() => setConvertTarget(q)}>
        {t('sales.quotes.action.convert')}
      </Button>,
    );
  }

  return (
    <section className="page">
      <DetailHeader
        backTo="/sales/quotes"
        title={q.quoteNumber}
        status={q.status}
        actions={actions}
        metrics={[
          { label: t('detail.metric.total'), value: formatCurrency(q.totalAmount, q.currency) },
          { label: t('detail.metric.date'), value: formatDate(q.quoteDate) },
          ...(q.validUntil
            ? [{ label: t('detail.metric.validUntil'), value: formatDate(q.validUntil) }]
            : []),
        ]}
      />

      <Card title={t('detail.header')}>
        <FieldGrid items={[
          { label: t('sales.quotes.modal.customer'), value: customer ? `${customer.code} - ${customer.name}` : q.customerId },
          { label: t('col.date'), value: formatDate(q.quoteDate) },
          { label: t('sales.quotes.col.validUntil'), value: formatDate(q.validUntil) },
          { label: t('vat.net'), value: <span className="mono">{formatCurrency(q.netAmount ?? q.totalAmount, q.currency)}</span> },
          { label: t('vat.tax'), value: <span className="mono">{formatCurrency(q.taxAmount ?? 0, q.currency)}</span> },
          { label: t('col.total'), value: <strong className="mono">{formatCurrency(q.totalAmount, q.currency)}</strong> },
          { label: t('detail.notes'), value: q.notes },
          { label: t('detail.created'), value: formatDateTime(q.createdAt) },
          { label: t('detail.updated'), value: formatDateTime(q.updatedAt) },
        ]} />
      </Card>

      <Card title={t('detail.lines')}>
        <Table
          rows={q.lines || []}
          getRowKey={(row) => row.id}
          columns={[
            { key: 'description', label: t('detail.lineDescription') },
            { key: 'quantity', label: t('detail.lineQty'), align: 'right', render: (r) => formatQty(t, r.quantity, r.unit) },
            { key: 'unitPrice', label: t('detail.lineUnitPrice'), align: 'right', render: (r) => formatCurrency(r.unitPrice, q.currency) },
            { key: 'taxRate', label: t('vat.rateCol'), align: 'right', render: (r) => `${Number(r.taxRate || 0).toFixed(1)} %` },
            { key: 'lineAmount', label: t('detail.lineTotal'), align: 'right', render: (r) => formatCurrency(r.lineAmount, q.currency) },
          ]}
        />
      </Card>

      <ConvertQuoteModal quote={convertTarget} onClose={() => setConvertTarget(null)} />
      <QuoteCreateModal open={editOpen} onClose={() => setEditOpen(false)} quote={q} />
      <QuoteSendModal quote={sendOpen ? q : null} onClose={() => setSendOpen(false)} />
    </section>
  );
}
